import { ExitToApp } from '@mui/icons-material';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Avatar,
    Box,
    Card,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@mui/material';
import { AbilityContext } from 'components/Functional/Can';
import { OrganizationsContext } from 'contexts/OrganizationsContext';
import { orderBy } from 'lodash';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    hiddenOrganizationUuidsSelector,
    primaryOrganizationUuidSelector,
    userSelector,
} from 'redux/reducers/user';
import ROUTES from 'Routes/routes';
import { getInitials } from 'utils';
const OrganizationList = ({
    onOrganizationClicked,
}: {
    onOrganizationClicked: ProfileDropdownProps['onOrganizationClicked'];
}) => {
    const { organizations } = useContext(OrganizationsContext);
    const ability = useContext(AbilityContext);
    const primaryOrganizationUuid = useSelector(primaryOrganizationUuidSelector);
    const hiddenOrganizationUuids = useSelector(hiddenOrganizationUuidsSelector);

    if (organizations?.filter((o) => ability.can('organization:update', o)).length == 0) {
        return null;
    }

    // Separate organizations into categories
    const visibleOrganizations = orderBy(organizations, ['name'], ['asc'])
        ?.filter((o) => ability.can('organization:update', o))
        ?.filter((o) => !hiddenOrganizationUuids?.includes(o.uuid));

    const hiddenOrganizations = orderBy(organizations, ['name'], ['asc'])
        ?.filter((o) => ability.can('organization:update', o))
        ?.filter((o) => hiddenOrganizationUuids?.includes(o.uuid));

    const renderOrganization = (organization: any) => {
        const isPrimary = organization.uuid === primaryOrganizationUuid;
        const isHidden = hiddenOrganizationUuids?.includes(organization.uuid);

        return (
            <ListItem
                onClick={() => onOrganizationClicked(organization)}
                button
                key={organization.uuid}
                sx={{
                    opacity: isHidden ? 0.5 : 1,
                    backgroundColor: isPrimary
                        ? 'action.selected'
                        : isHidden
                        ? 'action.hover'
                        : 'transparent',
                    '&:hover': {
                        backgroundColor: isPrimary ? 'action.selected' : 'action.hover',
                    },
                }}
            >
                <ListItemAvatar>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            variant="rounded"
                            src={organization.logo_url || ''}
                            alt={organization.name}
                            style={{
                                width: 40,
                                height: 40,
                                border: isPrimary ? '2px solid' : 'none',
                                borderColor: isPrimary ? 'secondary.main' : 'transparent',
                            }}
                        >
                            {getInitials(organization.name)}
                        </Avatar>

                        {/* Primary Organization Badge */}
                        {isPrimary && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    backgroundColor: 'secondary.main',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 2,
                                }}
                            >
                                <StarIcon
                                    sx={{
                                        fontSize: 14,
                                        color: 'common.white',
                                    }}
                                />
                            </Box>
                        )}

                        {/* Hidden Organization Icon */}
                        {isHidden && !isPrimary && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    backgroundColor: 'grey.500',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 2,
                                }}
                            >
                                <VisibilityOffIcon
                                    sx={{
                                        fontSize: 14,
                                        color: 'common.white',
                                    }}
                                />
                            </Box>
                        )}

                        {organization.subscription_tier?.name === 'Gold' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: '40px',
                                    height: '40px',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 16,
                                        backgroundColor: '#FFD700',
                                        width: '40px',
                                        height: '10px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '6px',
                                        fontWeight: 'bold',
                                        color: '#000',
                                        boxShadow: '0px 0px 1px rgba(0,0,0,0.1)',
                                        borderRadius: '0 0 2px 2px',
                                    }}
                                >
                                    GOLD
                                </Box>
                            </Box>
                        )}

                        {organization.subscription_tier?.name === 'Silver' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: '40px',
                                    height: '40px',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 16,
                                        backgroundColor: '#C0C0C0',
                                        width: '40px',
                                        height: '10px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '6px',
                                        fontWeight: 'bold',
                                        color: '#000',
                                        boxShadow: '0px 0px 1px rgba(0,0,0,0.1)',
                                        borderRadius: '0 0 2px 2px',
                                    }}
                                >
                                    SILVER
                                </Box>
                            </Box>
                        )}

                        {organization.subscription_tier?.name === 'Bronze' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: '40px',
                                    height: '40px',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 16,
                                        backgroundColor: '#CD7F32',
                                        width: '40px',
                                        height: '10px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '6px',
                                        fontWeight: 'bold',
                                        color: '#000',
                                        boxShadow: '0px 0px 1px rgba(0,0,0,0.1)',
                                        borderRadius: '0 0 2px 2px',
                                    }}
                                >
                                    BRONZE
                                </Box>
                            </Box>
                        )}
                    </Box>
                </ListItemAvatar>
                <ListItemText
                    primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isPrimary ? 700 : 400,
                        color: isHidden ? 'text.disabled' : 'text.primary',
                    }}
                    secondary={
                        isPrimary
                            ? 'Primary Organization'
                            : isHidden
                            ? 'Hidden from filters'
                            : undefined
                    }
                    secondaryTypographyProps={{
                        variant: 'caption',
                        color: isPrimary ? 'secondary.main' : 'text.secondary',
                    }}
                >
                    {organization.name}
                </ListItemText>
            </ListItem>
        );
    };

    return (
        <>
            {/* Primary Organization Section */}
            {visibleOrganizations?.some((o) => o.uuid === primaryOrganizationUuid) && (
                <>
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'secondary.main',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            ⭐ Primary Organization
                        </Typography>
                    </Box>
                    <List component="div">
                        {visibleOrganizations
                            ?.filter((o) => o.uuid === primaryOrganizationUuid)
                            ?.map((organization) => renderOrganization(organization))}
                    </List>
                    <Divider />
                </>
            )}

            {/* Regular Organizations Section */}
            {visibleOrganizations?.some((o) => o.uuid !== primaryOrganizationUuid) && (
                <>
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            Organizations
                        </Typography>
                    </Box>
                    <List component="div">
                        {visibleOrganizations
                            ?.filter((o) => o.uuid !== primaryOrganizationUuid)
                            ?.map((organization) => renderOrganization(organization))}
                    </List>
                    <Divider />
                </>
            )}

            {/* Hidden Organizations Section */}
            {hiddenOrganizations && hiddenOrganizations.length > 0 && (
                <>
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: 'text.disabled',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                            }}
                        >
                            👁️ Hidden Organizations
                        </Typography>
                    </Box>
                    <List component="div">
                        {hiddenOrganizations?.map((organization) =>
                            renderOrganization(organization),
                        )}
                    </List>
                    <Divider />
                </>
            )}
        </>
    );
};

interface ProfileDropdownProps {
    onLogOut: (action: string) => void;
    hideProfileOptions: () => void;
    onOrganizationClicked: (organization: any) => void;
}

const ProfileDropdown = ({
    onLogOut,
    hideProfileOptions,
    onOrganizationClicked,
}: ProfileDropdownProps) => {
    const { push } = useHistory();
    const { userData } = useSelector(userSelector);

    return (
        <Card sx={{ minWidth: 300 }} raised={false}>
            <List component="nav" dense>
                {userData && (
                    <>
                        <ListItem
                            button
                            onClick={() => {
                                hideProfileOptions();
                                push(ROUTES.Profile.path);
                            }}
                        >
                            <ListItemText
                                secondary={`${userData.user_name} ${userData.email}`}
                                primaryTypographyProps={{ variant: 'h6' }}
                            >
                                {`${userData.first_name} ${userData.last_name}`}
                            </ListItemText>
                            <SettingsIcon color="primary" />
                        </ListItem>
                    </>
                )}
                <ListItemButton onClick={() => push('/athletes')}>
                    <DirectionsRunIcon sx={{ mr: 4 }} color={'primary'} />
                    My Athlete Account
                </ListItemButton>
                <ListItemButton onClick={() => push('/media')}>
                    <PermMediaIcon sx={{ mr: 4 }} color={'primary'} />
                    Media Library
                </ListItemButton>
                <ListItemButton onClick={() => push('/Learn1TUL')}>
                    <CastForEducationIcon sx={{ mr: 4 }} color={'primary'} />
                    Learn 1TUL
                </ListItemButton>
            </List>
            <Divider />
            <OrganizationList onOrganizationClicked={onOrganizationClicked} />
            <List>
                <ListItem button onClick={() => onLogOut('log-out-all-devices')}>
                    <ListItemText>Logout All Devices</ListItemText>
                </ListItem>
                <ListItem button onClick={() => onLogOut('log-out')}>
                    <ListItemText>Logout</ListItemText>
                    <ListItemSecondaryAction>
                        <ExitToApp />
                    </ListItemSecondaryAction>
                </ListItem>
            </List>
        </Card>
    );
};

export default ProfileDropdown;
